import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'shared/components/ui/dialog';
import React, {
 useContext, useEffect, useState
} from 'react';
import {
 ScrollArea
} from 'shared/components/ui/scroll-area';
import {
 Button
} from 'shared/components/ui/button';
import {
 Context
} from 'shared/context/GlobalState';
import {
 ChevronDown
} from 'lucide-react';
import {
 SearchBar
} from 'builders/builderInternal/components/Instructions/Filters';
import {
 formatDateToDDMMYYY
} from 'shared/utils/timeUtils';
import {
 OrganizationContext
} from 'shared/context/OrganizationContext';
import {
 Opcode
} from '../types/builderAssistant.types';
interface OpCodeSelectionModalProps {
  opcodes: Opcode[];
  disabled: boolean;
  onOpcodeSelect: (res: string | string[]) => void;
  defaultSelectedOpcode?: string;
  defaultSelectedOpcodes?: string[];
  multiSelect?: boolean;
}

const OpCodeSelectionModal = ({
  opcodes = [],
  disabled,
  onOpcodeSelect,
  defaultSelectedOpcode = '',
  defaultSelectedOpcodes = [],
  multiSelect = false,
}: OpCodeSelectionModalProps) => {
  const [opcodeSelectObj, setOpcodeSelectObj] = useState<Opcode | null>(null);
  const [selectedOpcodes, setSelectedOpcodes] = useState<string[]>([]);
  const [selectedOpcode, setSelectedOpcode] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const {
 addNewNotifcation
} = useContext(Context);
  const {
 selectedOrganization
} = useContext(OrganizationContext);

  useEffect(() => {
    if (multiSelect) {
      if (defaultSelectedOpcodes) {
        setSelectedOpcodes(defaultSelectedOpcodes);
        const defaultOpcode = opcodes.find(
          (item) => item.opcode_id === defaultSelectedOpcode
        );
        if (defaultOpcode) {
          setOpcodeSelectObj(defaultOpcode);
        }
      }
    } else {
      if (defaultSelectedOpcode) {
        setSelectedOpcode(defaultSelectedOpcode);
      }
    }
  }, [defaultSelectedOpcode, defaultSelectedOpcodes, multiSelect, opcodes]);

  const handleOpcodeSelect = (opcode: Opcode) => {
    if (multiSelect) {
      if (selectedOpcodes.includes(opcode.opcode_id)) {
        setSelectedOpcodes((prev) =>
          prev.filter((item) => item !== opcode.opcode_id)
        );
      } else {
        setSelectedOpcodes((prev) => [...prev, opcode.opcode_id]);
      }
      setSelectedOpcode(opcode.opcode_id);
      setOpcodeSelectObj(opcode);
    } else {
      if (selectedOpcode === opcode.opcode_id) {
        resetOpcodeDetails();
      } else {
        setSelectedOpcode(opcode.opcode_id);
        setOpcodeSelectObj(opcode);
        // For single select, immediately select the opcode
        onOpcodeSelect(opcode.opcode_id);
      }
    }
  };

  const resetOpcodeDetails = () => {
    setSelectedOpcode(null);
    setOpcodeSelectObj(null);
  };

  const handleDone = () => {
    if (multiSelect) {
      if (selectedOpcodes.length === 0) {
        addNewNotifcation(
          'Please select at least one opcode first.',
          'warning'
        );
        return;
      }
      onOpcodeSelect(selectedOpcodes);
    } else {
      if (!selectedOpcode) {
        addNewNotifcation('Please select an opcode first.', 'warning');
        return;
      }
      onOpcodeSelect(selectedOpcode);
    }
  };

  const filterList = (list: Opcode[]) => {
      return list.filter((opcode) =>
        opcode.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opcode.opcode_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const getCreatedBy = (createBy: string) => {
    const org = selectedOrganization as any;

    if (org?.superAdmin?._id === createBy) {
      return org.superAdmin?.name || '-';
    } else {
      const member = org?.members?.find(
        (item: any) => item?.id?._id === createBy
      );
      return member?.id?.name || '-';
    }
  };

  const getTriggerDisplayText = () => {
    if (disabled) return 'loading...';

    if (multiSelect) {
      if (defaultSelectedOpcodes.length === 0) return 'Select opcodes';

      const selectedNames = defaultSelectedOpcodes
        .map(id => opcodes.find(op => op.opcode_id === id)?.name || id);

      if (selectedNames.length <= 2) {
        return selectedNames.join(', ');
      }
      return `${selectedNames.length} opcodes selected`;
    } else {
      if (!defaultSelectedOpcode) return 'Select an opcode';
      const selectedName = opcodes.find(op => op.opcode_id === defaultSelectedOpcode)?.name || defaultSelectedOpcode;
      return <span>{selectedName}</span>;
    }
  };

  // Single flat list — the classic/json backend distinction is hidden from
  // the user (feedback 2026-04-24_10-53: opcodes have no "types" in the UI).
  const renderOpcodeList = (filteredList: Opcode[]) => (
      <div className="grid grid-cols-3 grid-rows-5 gap-4 p-4">
          {filteredList.length > 0 ? (
        filteredList.map((opcode) => (
            <Button
            key={opcode.opcode_id}
            variant={
              multiSelect
                ? selectedOpcodes.includes(opcode.opcode_id)
                  ? 'default'
                  : 'outline'
                : selectedOpcode === opcode.opcode_id
                ? 'default'
                : 'outline'
            }
            onClick={() => handleOpcodeSelect(opcode)}
            className="justify-start h-auto text-left overflow-hidden px-2"
          >
                <div className="flex flex-grow flex-col w-full">
                    <div className="font-semibold w-full truncate">
                        {opcode.name}
                    </div>
                </div>
            </Button>
        ))
      ) : (
          <p>No opcodes available</p>
      )}
      </div>
  );

  return (
      <Dialog>
          <DialogTrigger asChild>
              <Button disabled={disabled} variant="outline" className="w-full">
                  <ChevronDown className="mr-2 h-4 w-4" />
                  {getTriggerDisplayText()}
              </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[90vw] sm:max-h-[90vh]">
              <DialogHeader>
                  <DialogTitle>Select an Opcode</DialogTitle>
                  <DialogDescription>
                      Choose an opcode and see its details.
                  </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                  <div className="flex flex-col md:flex-row gap-4">
                      <ScrollArea className="h-[450px] flex-grow">
                          <div className="mb-2 flex flex-col sm:flex-row gap-4 items-center p-4 pb-0">
                              <div className="flex-grow w-full">
                                  <SearchBar onSearch={handleSearch} />
                              </div>
                          </div>

                          {renderOpcodeList(filterList(opcodes))}
                      </ScrollArea>
                      {selectedOpcode && opcodeSelectObj && (
                      <div className="md:w-1/3 p-4 bg-muted rounded-md h-96 max-h-96 overflow-y-auto">
                          <h2 className="mb-2 font-semibold text-xl">{opcodeSelectObj.name || selectedOpcode}</h2>

                          {opcodeSelectObj.description && (
                          <div className="mb-4">
                              <p>{opcodeSelectObj.description}</p>
                          </div>
                )}

                          <div className="mb-4">
                              <h4 className="font-semibold">Created:</h4>
                              <p>
                                  {opcodeSelectObj.created
                      ? formatDateToDDMMYYY(opcodeSelectObj.created)
                      : '-'}
                              </p>
                          </div>

                          {opcodeSelectObj.updated && (
                          <div className="mb-4">
                              <h4 className="font-semibold">Updated:</h4>
                              <p>
                                  {opcodeSelectObj.updated
                        ? formatDateToDDMMYYY(opcodeSelectObj.updated)
                        : '-'}
                              </p>
                          </div>
                )}
                          {opcodeSelectObj.createdBy && (
                          <div className="mb-4">
                              <h4 className="font-semibold">Created by:</h4>
                              <p>{getCreatedBy(opcodeSelectObj.createdBy) || '-'}</p>
                          </div>
                )}
                      </div>
            )}
                  </div>
                  <div className="flex justify-end space-x-2">
                      <DialogClose asChild>
                          <Button type="button" variant="secondary">
                              Close
                          </Button>
                      </DialogClose>
                      {multiSelect
              ? selectedOpcodes.length > 0 && (
                  <div className="flex justify-end">
                      <DialogTrigger asChild>
                          <Button variant="default" onClick={handleDone}>
                              Select
                          </Button>
                      </DialogTrigger>
                  </div>
                )
              : selectedOpcode && (
                  <div className="flex justify-end">
                      <DialogTrigger asChild>
                          <Button variant="default">Done</Button>
                      </DialogTrigger>
                  </div>
                )}
                  </div>
              </div>
          </DialogContent>
      </Dialog>
  );
};

export default OpCodeSelectionModal;
